import React, { useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Page,
  Button,
  LegacyCard,
  IndexTable,
  Text,
  TextField,
  BlockStack,
  Spinner,
  Pagination,
  Badge,
  Select,
} from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import { formatPrice, formatDate } from "../../utility/funcs";
import { invoiceDue } from "../../utility/invoicing";
import DateRangeFilter from "../Reports/DateRangeFilter";
import InvoiceActionsModal from "./InvoiceActionsModal";
import PaymentDetailModal from "./PaymentDetailModal";
import * as XLSX from "xlsx/xlsx.mjs";
import {
  applyHeaderFormats,
  moneyHeaders,
  toExcelSerial,
  DATE_ONLY_FMT,
} from "../../utility/xlsxExport";

const PAGE_SIZE = 25;

function statusBadge(inv) {
  const due = invoiceDue(inv);
  if (inv.status === "void") return <Badge tone="critical">Cancelada</Badge>;
  if (inv.status === "draft") return <Badge tone="info">Borrador</Badge>;
  if (due <= 0.009) return <Badge tone="success">Pagada</Badge>;
  if ((inv.amount_paid || 0) + (inv.amount_credited || 0) > 0)
    return <Badge tone="attention">Parcial</Badge>;
  return <Badge tone="warning">Por cobrar</Badge>;
}

function Invoices() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [invoiceActions, setInvoiceActions] = useState({ open: false, invoice: null });
  // Invoice id whose reservations detail is shown; null = modal closed.
  const [detailInvoiceId, setDetailInvoiceId] = useState(null);

  const page = parseInt(searchParams.get("page")) || 1;
  const setPage = (newPage) => {
    const params = new URLSearchParams(searchParams);
    if (newPage === 1) params.delete("page");
    else params.set("page", String(newPage));
    setSearchParams(params);
  };

  const fetchInvoices = async () => {
    setLoading(true);
    const fromIdx = (page - 1) * PAGE_SIZE;
    const toIdx = fromIdx + PAGE_SIZE - 1;

    const { data, error } = await supabase.rpc("search_invoices", {
      search_text: search.trim() || null,
      from_index: fromIdx,
      to_index: toIdx,
      date_from: dateFrom || null,
      date_to: dateTo || null,
      status_filter: statusFilter === "all" ? null : statusFilter,
    });

    if (error) {
      console.error(error.message);
      setLoading(false);
      return;
    }
    setInvoices(data || []);
    setTotal(data?.[0]?.total_count || 0);
    setLoading(false);
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, dateFrom, dateTo]);

  const STATUS_ES = {
    closed: "Pagada",
    open: "Por cobrar",
    draft: "Borrador",
    void: "Cancelada",
  };

  // Export the full filtered set (not just the current page) to Excel.
  const exportExcel = async () => {
    setExporting(true);
    try {
      const { data, error } = await supabase.rpc("search_invoices", {
        search_text: search.trim() || null,
        from_index: 0,
        to_index: 100000,
        date_from: dateFrom || null,
        date_to: dateTo || null,
        status_filter: statusFilter === "all" ? null : statusFilter,
      });
      if (error) {
        alert("Error al exportar: " + error.message);
        return;
      }
      const cleaned = (data || []).map((inv) => {
        const due = Math.max(0, invoiceDue(inv));
        const reservas = inv.reservation_id
          ? `#${inv.reservation_id}`
          : (inv.reservation_ids || []).map((n) => `#${n}`).join(", ");
        return {
          Folio: inv.invoice_number || "",
          "ID Alegra": inv.alegra_id || "",
          Fecha: toExcelSerial(inv.invoice_date) ?? "",
          "Periodo inicio": toExcelSerial(inv.period_from) ?? "",
          "Periodo fin": toExcelSerial(inv.period_to) ?? "",
          "Cliente / Empresa": inv.recipient || "",
          RFC: inv.rfc || "",
          Reservas: reservas,
          Creador: inv.reservation_creator || "",
          Subtotal: inv.subtotal || 0,
          IVA: inv.tax || 0,
          Total: inv.total || 0,
          Pagado: inv.amount_paid || 0,
          "Notas de crédito": inv.amount_credited || 0,
          Saldo: due,
          "Uso CFDI": inv.cfdi_use || "",
          "Forma de pago": inv.payment_method || "",
          "Método de pago": inv.payment_type || "",
          Estado: STATUS_ES[inv.status] || inv.status || "",
        };
      });
      const ws = XLSX.utils.json_to_sheet(cleaned);
      // Money columns land as real numeric cells with a currency format, so the
      // sheet can be summed; the date columns get a date format.
      applyHeaderFormats(ws, {
        Fecha: DATE_ONLY_FMT,
        "Periodo inicio": DATE_ONLY_FMT,
        "Periodo fin": DATE_ONLY_FMT,
        ...moneyHeaders(["Subtotal", "IVA", "Total", "Pagado", "Notas de crédito", "Saldo"]),
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Facturas");
      const span = dateFrom && dateTo ? ` ${dateFrom} a ${dateTo}` : "";
      XLSX.writeFile(wb, `Facturas${span} - GoMove.xlsx`);
    } finally {
      setExporting(false);
    }
  };

  const submitSearch = (e) => {
    e?.preventDefault?.();
    if (page !== 1) setPage(1);
    else fetchInvoices();
  };

  const hasNext = page * PAGE_SIZE < total;

  const rows = invoices.map((inv, index) => {
    const targetResId =
      inv.reservation_id || inv.reservation_ids?.[0];
    const onRowClick = () => {
      if (targetResId) navigate(`/reservation?id=${targetResId}`);
    };
    return (
      <IndexTable.Row
        id={String(inv.id)}
        key={inv.id}
        position={index}
        onClick={onRowClick}
      >
        <IndexTable.Cell>
          <span
            role="button"
            style={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={(e) => {
              e.stopPropagation();
              setInvoiceActions({ open: true, invoice: inv });
            }}
          >
            <Text fontWeight="semibold">
              {inv.invoice_number || `#${inv.alegra_id || inv.id}`}
            </Text>
          </span>
        </IndexTable.Cell>
        <IndexTable.Cell>{formatDate(inv.invoice_date)}</IndexTable.Cell>
        <IndexTable.Cell>
          <span title={inv.recipient}>{inv.recipient || "-"}</span>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text variant="bodySm" tone="subdued">
            {inv.rfc || "-"}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          {(() => {
            const linked = inv.reservation_ids || [];
            const count = inv.reservation_id ? 1 : linked.length;
            if (count === 0) return "-";
            const label =
              count === 1
                ? `#${inv.reservation_id || linked[0]}`
                : "Ver detalle";
            return (
              <span
                role="button"
                style={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setDetailInvoiceId(inv.id);
                }}
              >
                {label}
              </span>
            );
          })()}
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text variant="bodySm">{inv.reservation_creator || "-"}</Text>
        </IndexTable.Cell>
        <IndexTable.Cell>{formatPrice(inv.total)}</IndexTable.Cell>
        <IndexTable.Cell>
          {formatPrice(inv.amount_paid || 0)}
          {inv.amount_credited > 0.01 && (
            <Text variant="bodySm" tone="subdued">NC {formatPrice(inv.amount_credited)}</Text>
          )}
        </IndexTable.Cell>
        <IndexTable.Cell>
          {(() => {
            const due = invoiceDue(inv);
            if (due <= 0.009) return formatPrice(0);
            return (
              <Text tone="critical" fontWeight="semibold">
                {formatPrice(due)}
              </Text>
            );
          })()}
        </IndexTable.Cell>
        <IndexTable.Cell>
          {statusBadge(inv)}{" "}
          {inv.payment_type ? (
            <Badge size="small" tone={inv.payment_type === "PPD" ? "warning" : undefined}>
              {inv.payment_type}
            </Badge>
          ) : null}
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <AppWrapper>
      <Page
        title="Facturas"
        primaryAction={
          <Button
            variant="primary"
            onClick={() => navigate("/invoicing/create")}
          >
            Nueva Factura
          </Button>
        }
        secondaryActions={
          <Button loading={exporting} onClick={exportExcel}>
            Exportar Excel
          </Button>
        }
      >
        <BlockStack gap={400}>
          <form onSubmit={submitSearch}>
            <div style={{ display: "flex", gap: "0.25rem" }}>
              <span style={{ flexGrow: 1 }}>
                <TextField
                  autoComplete="off"
                  placeholder="Buscar por folio, cliente, RFC, # de reserva o ID Alegra"
                  value={search}
                  onChange={setSearch}
                  disabled={loading}
                />
              </span>
              <span
                style={{
                  aspectRatio: 1,
                  display: "flex",
                  alignItems: "center",
                  width: 32,
                  height: 32,
                }}
              >
                <Button icon={SearchIcon} submit disabled={loading} />
              </span>
            </div>
          </form>

          <div style={{ display: "flex", gap: "0.5rem", alignItems: "end" }}>
            <DateRangeFilter
              label="Periodo"
              from={dateFrom}
              to={dateTo}
              onChange={({ from, to }) => {
                setDateFrom(from);
                setDateTo(to);
                if (page !== 1) setPage(1);
              }}
            />
            <Select
              label="Estado"
              options={[
                { label: "Todas", value: "all" },
                { label: "Por cobrar", value: "open" },
                { label: "Pagadas", value: "paid" },
                { label: "Canceladas", value: "void" },
              ]}
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                if (page !== 1) setPage(1);
              }}
            />
            <Button
              onClick={() => {
                if (page !== 1) setPage(1);
                else fetchInvoices();
              }}
            >
              Filtrar
            </Button>
            {(dateFrom || dateTo || search || statusFilter !== "all") && (
              <Button
                variant="plain"
                onClick={() => {
                  setSearch("");
                  setDateFrom("");
                  setDateTo("");
                  setStatusFilter("all");
                  if (page !== 1) setPage(1);
                  else setTimeout(fetchInvoices, 0);
                }}
              >
                Limpiar
              </Button>
            )}
          </div>

          {loading ? (
            <Spinner size="small" />
          ) : (
            <LegacyCard>
              <IndexTable
                resourceName={{ singular: "Factura", plural: "Facturas" }}
                selectable={false}
                itemCount={invoices.length}
                headings={[
                  { title: "Folio" },
                  { title: "Fecha" },
                  { title: "Cliente / Empresa" },
                  { title: "RFC" },
                  { title: "Reserva" },
                  { title: "Creador" },
                  { title: "Total" },
                  { title: "Pagado" },
                  { title: "Saldo" },
                  { title: "Estado" },
                ]}
              >
                {rows}
              </IndexTable>
              <div
                style={{
                  padding: "1rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text variant="bodySm" tone="subdued">
                  {total > 0
                    ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(
                        page * PAGE_SIZE,
                        total
                      )} de ${total}`
                    : "Sin resultados"}
                </Text>
                <Pagination
                  hasPrevious={page > 1}
                  onPrevious={() => setPage(page - 1)}
                  hasNext={hasNext}
                  onNext={() => setPage(page + 1)}
                />
              </div>
            </LegacyCard>
          )}
        </BlockStack>
      </Page>
      <InvoiceActionsModal
        open={invoiceActions.open}
        invoice={invoiceActions.invoice}
        onClose={() => setInvoiceActions({ open: false, invoice: null })}
        onChanged={fetchInvoices}
      />
      <PaymentDetailModal
        open={detailInvoiceId != null}
        invoiceId={detailInvoiceId}
        onClose={() => setDetailInvoiceId(null)}
      />
    </AppWrapper>
  );
}

export default Invoices;
